/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { useSettings } from '../hooks/useSettings';
import { toWhatsAppUrl, toTelUrl } from '../api/settings';
import '../styles/Contact.css';

function Contact() {
  const [form, setForm] = useState({
    name: '',
    mobile: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isValid =
    form.name.trim().length >= 2 &&
    form.mobile.length === 10 &&
    form.subject &&
    form.message.trim().length >= 10;

  function handleMobile(e) {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    setForm(f => ({ ...f, mobile: val }));
  }

  function handleSubmit() {
    if (!isValid) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitted(true);
      setSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1600);
  }

  const { settings } = useSettings();

  return (
    <>
      <Header />

      {/* PAGE HEADER */}
      <div className="page-header">
        <div className="page-header-inner">
          <div className="page-eyebrow">
            <i className="ti ti-headset" style={{fontSize:'13px'}}></i> Get in Touch
          </div>
          <h1>Contact Us</h1>
          <p className="page-header-sub">Have a question, feedback, or need help with an order? We're here 7 days a week. Reach us on WhatsApp for the fastest response.</p>
        </div>
      </div>

      {/* LAYOUT */}
      <div className="ct-layout">

        {/* FORM CARD */}
        <div className="form-card">
          <div className="form-card-header">
            <div className="form-card-title">Send us a message</div>
            <div className="form-card-sub">We typically respond within a few hours on business days.</div>
          </div>

          <div className="form-card-body">
            {submitted ? (
              <div className="success-state">
                <div className="success-circle"><i className="ti ti-check"></i></div>
                <div className="success-title">Message sent!</div>
                <div className="success-desc">Thank you for reaching out. Our team will get back to you within a few hours. For urgent help, WhatsApp us directly.</div>
                <a className="btn-back" href={toWhatsAppUrl(settings.whatsappNumber)}>
                  <i className="ti ti-brand-whatsapp" style={{fontSize:'16px'}}></i>
                  Chat on WhatsApp
                </a>
              </div>
            ) : (
              <>
                <div className="form-row">
                  {/* Full Name */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="name">Full name <span>*</span></label>
                    <input
                      className="form-input"
                      type="text"
                      id="name"
                      placeholder="Your name"
                      autoComplete="name"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    />
                  </div>

                  {/* Mobile */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="mobile">Mobile number <span>*</span></label>
                    <div className="input-wrap">
                      <span className="input-prefix">+91</span>
                      <input
                        className="form-input with-prefix"
                        type="tel"
                        id="mobile"
                        placeholder="98765 43210"
                        maxLength={10}
                        autoComplete="tel"
                        value={form.mobile}
                        onChange={handleMobile}
                      />
                    </div>
                  </div>
                </div>

                {/* Subject */}
                <div className="form-group">
                  <label className="form-label" htmlFor="subject">Subject <span>*</span></label>
                  <select
                    className="form-input"
                    id="subject"
                    value={form.subject}
                    onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                  >
                    <option value="" disabled>Select a topic</option>
                    <option value="order_issue">Issue with my order</option>
                    <option value="delivery">Delivery question</option>
                    <option value="product">Product / availability query</option>
                    <option value="payment">Payment or refund</option>
                    <option value="varmala">Wedding varmala enquiry</option>
                    <option value="feedback">General feedback</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Message */}
                <div className="form-group">
                  <label className="form-label" htmlFor="message">Message <span>*</span></label>
                  <textarea
                    className="form-input"
                    id="message"
                    placeholder="Describe your question or feedback in detail…"
                    rows={5}
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  />
                </div>

                <button className="btn-primary" disabled={!isValid || submitting} onClick={handleSubmit}>
                  {submitting ? (
                    <>
                      <i className="ti ti-loader-2" style={{animation:'spin 1s linear infinite', fontSize:'18px'}}></i>
                      Sending…
                    </>
                  ) : (
                    <>
                      <i className="ti ti-send"></i>
                      Send message
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        {/* RIGHT SIDEBAR */}
        <div className="ct-sidebar">

          {/* DIRECT CONTACT */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-title">
                <i className="ti ti-phone"></i> Direct contact
              </div>
            </div>
            <div className="info-card-body">
              <a className="contact-method" href={toWhatsAppUrl(settings.whatsappNumber)}>
                <div className="cm-icon"><i className="ti ti-brand-whatsapp"></i></div>
                <div className="cm-text">
                  <div className="cm-hint">Fastest response — WhatsApp</div>
                  <div className="cm-label">{settings.whatsappNumber}</div>
                </div>
              </a>
              <a className="contact-method" href={toTelUrl(settings.contactNumber)}>
                <div className="cm-icon"><i className="ti ti-phone-call"></i></div>
                <div className="cm-text">
                  <div className="cm-hint">Call us directly</div>
                  <div className="cm-label">{settings.contactNumber}</div>
                </div>
              </a>
              <a className="contact-method" href={`mailto:${settings.email}`}>
                <div className="cm-icon"><i className="ti ti-mail"></i></div>
                <div className="cm-text">
                  <div className="cm-hint">Email support</div>
                  <div className="cm-label">{settings.email}</div>
                </div>
              </a>
            </div>
          </div>

          {/* BUSINESS HOURS */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-title">
                <i className="ti ti-clock"></i> Business hours
              </div>
            </div>
            <div className="info-card-body">
              <div className="hours-row">
                <span className="hours-day">Monday – Friday</span>
                <span className="hours-time">8 AM – 9 PM</span>
              </div>
              <div className="hours-row">
                <span className="hours-day">Saturday</span>
                <span className="hours-time">8 AM – 9 PM</span>
              </div>
              <div className="hours-row">
                <span className="hours-day">Sunday</span>
                <span className="hours-time">8 AM – 9 PM</span>
              </div>
              <div className="hours-row">
                <span className="hours-day">Festivals &amp; holidays</span>
                <span className="hours-badge"><i className="ti ti-check" style={{fontSize:'10px'}}></i> Open</span>
              </div>
            </div>
          </div>

          {/* ADDRESS */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-title">
                <i className="ti ti-map-pin"></i> Based in Gujarat
              </div>
            </div>
            <div className="info-card-body">
              <div className="address-block">
                <div className="addr-icon"><i className="ti ti-building-store"></i></div>
                <div className="addr-text">
                  <div className="addr-title">Sadguru Flower</div>
                  <div className="addr-line">
                    Morbi, Gujarat, India<br />
                    Delivering across Morbi, Rajkot,<br />
                    Jamnagar &amp; Halvad
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* DELETE REQUEST LINK */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-title">
                <i className="ti ti-shield"></i> Privacy &amp; data
              </div>
            </div>
            <div className="info-card-body">
              <p style={{fontSize:'13px', color:'var(--text-muted)', lineHeight:1.6, marginBottom:'12px'}}>
                Want to delete your account or request your data? Use our dedicated form.
              </p>
              <Link
                to="/delete-request"
                style={{
                  display:'inline-flex', alignItems:'center', gap:'6px',
                  fontSize:'13px', fontWeight:500, color:'var(--brand)',
                  textDecoration:'none'
                }}
              >
                <i className="ti ti-trash" style={{fontSize:'14px'}}></i>
                Submit a data deletion request
              </Link>
            </div>
          </div>

        </div>
      </div>

      {/* FOOTER */}
      <footer className="ct-footer">
        <p>© 2026 Sadguru Flower. All rights reserved.</p>
        <div className="ct-footer-links">
          <Link to="/">Home</Link>
          <Link to="/privacy">Privacy policy</Link>
          <Link to="/delete-request">Delete my data</Link>
        </div>
      </footer>
    </>
  );
}

export default Contact;
